'use client';

import { isBefore, startOfDay } from 'date-fns';
import { Calendar, History, Loader2, Package } from 'lucide-react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { JSX, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { parseDateOnly } from '@/lib/utils/date-only';

import type { EmployeeOrder, OrderTimeFilter } from '../types';
import { OrderCard } from './order-card';

interface OrdersListProps {
  orders: EmployeeOrder[];
  isLoading: boolean;
  error: Error | null;
}

export function OrdersList({ orders, isLoading, error }: OrdersListProps): JSX.Element {
  const t = useTranslations('employeeOrders');
  const [filter, setFilter] = useState<OrderTimeFilter>('upcoming');

  const { filteredOrders, upcomingCount, pastCount } = useMemo(() => {
    const today = startOfDay(new Date());
    let upcoming = 0;
    let past = 0;
    const upcomingOrders: EmployeeOrder[] = [];
    const pastOrders: EmployeeOrder[] = [];

    for (const order of orders) {
      const orderDate = parseDateOnly(order.meal_date);
      const isUpcoming = !isBefore(orderDate, today) && order.status !== 'cancelled';
      const isPast =
        isBefore(orderDate, today) || order.status === 'delivered' || order.status === 'cancelled';

      if (isUpcoming) {
        upcoming++;
        upcomingOrders.push(order);
      }
      if (isPast) {
        past++;
        pastOrders.push(order);
      }
    }

    const selected = filter === 'upcoming' ? upcomingOrders : pastOrders;
    return { filteredOrders: selected, upcomingCount: upcoming, pastCount: past };
  }, [orders, filter]);

  const sortedOrders = useMemo(() => {
    return [...filteredOrders].sort((a, b) => {
      const dateA = parseDateOnly(a.meal_date).getTime();
      const dateB = parseDateOnly(b.meal_date).getTime();

      if (filter === 'upcoming') {
        return dateA - dateB;
      }
      return dateB - dateA;
    });
  }, [filteredOrders, filter]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <p>{t('error')}</p>
      </div>
    );
  }

  return (
    <Tabs value={filter} onValueChange={(v) => setFilter(v as OrderTimeFilter)}>
      <TabsList className="mb-4 grid w-full grid-cols-2">
        <TabsTrigger value="upcoming" className="gap-2 max-xs:gap-1 max-xs:px-1.5 max-xs:text-xs">
          <Package className="h-4 w-4 max-xs:hidden" />
          {t('tabs.upcoming')}
          {upcomingCount > 0 && (
            <span className="rounded-full bg-primary/20 px-1.5 py-0.5 text-xs max-xs:px-1 max-xs:text-[10px]">
              {upcomingCount}
            </span>
          )}
        </TabsTrigger>
        <TabsTrigger value="past" className="gap-2 max-xs:gap-1 max-xs:px-1.5 max-xs:text-xs">
          <History className="h-4 w-4 max-xs:hidden" />
          {t('tabs.past')}
          {pastCount > 0 && (
            <span className="rounded-full bg-muted px-1.5 py-0.5 text-xs max-xs:px-1 max-xs:text-[10px]">
              {pastCount}
            </span>
          )}
        </TabsTrigger>
      </TabsList>

      <TabsContent value="upcoming" className="mt-0">
        {sortedOrders.length === 0 ? (
          <EmptyState type="upcoming" />
        ) : (
          <div className="space-y-3">
            {sortedOrders.map((order) => (
              <OrderCard key={order.order_slot_id} order={order} />
            ))}
          </div>
        )}
      </TabsContent>

      <TabsContent value="past" className="mt-0">
        {sortedOrders.length === 0 ? (
          <EmptyState type="past" />
        ) : (
          <div className="space-y-3">
            {sortedOrders.map((order) => (
              <OrderCard key={order.order_slot_id} order={order} />
            ))}
          </div>
        )}
      </TabsContent>
    </Tabs>
  );
}

function EmptyState({ type }: { type: 'upcoming' | 'past' }) {
  const t = useTranslations('employeeOrders');
  const Icon = type === 'upcoming' ? Package : History;

  return (
    <div className="pb-12 pt-16 text-center text-muted-foreground max-xs:pb-8 max-xs:pt-10">
      {/* Icon with subtle background - profile page pattern */}
      <span
        className={cn(
          'inline-flex items-center justify-center',
          'mx-auto mb-4 h-16 w-16 rounded-2xl max-xs:h-12 max-xs:w-12',
          'bg-muted/50',
          'transition-colors duration-200',
        )}
      >
        <Icon className="h-8 w-8 text-muted-foreground/70 max-xs:h-6 max-xs:w-6" />
      </span>
      <p className="font-medium text-foreground">
        {type === 'upcoming' ? t('empty.upcomingTitle') : t('empty.pastTitle')}
      </p>
      <p className="mx-auto mt-1.5 max-w-[260px] text-sm text-muted-foreground/70 max-xs:text-xs">
        {type === 'upcoming' ? t('empty.upcomingDescription') : t('empty.pastDescription')}
      </p>
      {type === 'upcoming' && (
        <Link href="/app/menu-selection" className="inline-block mt-6">
          <Button variant="outline" className="min-h-[44px]">
            <Calendar className="h-4 w-4" />
            {t('empty.browseMenu')}
          </Button>
        </Link>
      )}
    </div>
  );
}
